<template>
	<div class="flex w-full flex-col gap-10">
		<div v-if="type === 'signin' && step === 'credentials'" class="flex justify-start">
			<Logo type="large" class="h-5.5! md:h-7!" />
		</div>

		<div v-if="title || subtitle" class="flex flex-col gap-3">
			<div v-if="title" class="font-display text-fs-xl font-medium" data-test="login-title">
				{{ title }}
			</div>
			<div v-if="subtitle" class="text-secondary">{{ subtitle }}</div>
		</div>

		<ElementsTransition type="fade-up" :duration="250">
			<SignIn v-if="type === 'signin'" key="signin" ref="signInRef" @update:step="step = $event">
				<template #prefix-actions>
					<div class="mb-6 flex w-full flex-wrap gap-1">
						<span>{{ $t(`auth.login.forgot_password`) }}</span>
						<JMButton text type="primary" size="large" @click="gotoForgotPassword()">
							{{ $t(`common.misc.click_here`) }}
						</JMButton>
					</div>
				</template>
				<template v-if="showSignupLink" #suffix-actions>
					<JMButton type="secondary" size="large" @click="gotoSignup()">
						{{ $t(`auth.login.register_link`) }}
					</JMButton>
				</template>
			</SignIn>
			<DataExport v-else-if="type === 'dataexport'" key="dataexport" />
			<Secretary v-else-if="type === 'secretary'" key="secretary" />
			<ForgotPassword v-else-if="type === 'forgotpassword'" key="forgotpassword" />
			<ResetPassword v-else-if="type === 'resetpassword'" key="resetpassword" />
		</ElementsTransition>
	</div>
</template>

<script lang="ts" setup>
import type { FormStep, FormType } from "./types"
import type { I18nString } from "@/lang/config"
import { computed, defineAsyncComponent, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import Logo from "@/app-layouts/common/Logo.vue"
import ElementsTransition from "@/components/common/ElementsTransition.vue"
import JMButton from "@/components/common/JMButton.vue"

const { showSignupLink } = defineProps<{
	showSignupLink?: boolean
}>()

const router = useRouter()

const type = defineModel<FormType>("type", { default: "signin" })
const step = defineModel<FormStep>("step", { default: "credentials" })
const signInRef = ref<InstanceType<typeof SignIn> | null>(null)
const ForgotPassword = defineAsyncComponent(() => import("./ForgotPassword.vue"))
const ResetPassword = defineAsyncComponent(() => import("./ResetPassword.vue"))
const SignIn = defineAsyncComponent(() => import("./SignIn.vue"))
const Secretary = defineAsyncComponent(() => import("./Secretary.vue"))
const DataExport = defineAsyncComponent(() => import("./DataExport.vue"))

const { t } = useI18n()

const title = computed<string | null>(() => {
	switch (type.value) {
		case "signin":
			if (step.value === "mfa") {
				return null
			}
			return t<I18nString>(`auth.login.title`)
		case "forgotpassword":
			return t<I18nString>(`auth.forgot_password.title`)
		case "resetpassword":
			return t<I18nString>(`auth.reset_password.title`)
		case "secretary":
			return t<I18nString>(`auth.secretary_password.title`)
		case "dataexport":
			return t<I18nString>(`auth.data_export.title`)
		default:
			return null
	}
})

const subtitle = computed<string | null>(() => {
	switch (type.value) {
		case "signin":
			if (step.value === "mfa") {
				return null
			}
			return t<I18nString>(`auth.login.subtitle`)
		case "forgotpassword":
			return t<I18nString>(`auth.forgot_password.subtitle`)
		case "secretary":
			return t<I18nString>(`auth.secretary_password.subtitle`)
		case "resetpassword":
			return t<I18nString>(`auth.reset_password.subtitle`)
		case "dataexport":
			return t<I18nString>(`auth.data_export.subtitle`)
		default:
			return null
	}
})

function gotoForgotPassword() {
	type.value = "forgotpassword"
}

function gotoSignup() {
	router.push({ name: "Register" })
}

watch(step, val => {
	if (val === "mfa") {
		signInRef.value?.setStep("mfa")
	}
	if (val === "credentials") {
		signInRef.value?.setStep("credentials")
	}
})
</script>
